import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import cN from 'classnames';
import styles from './styles.module.scss';
import { filterTypesKey, I_FilterItem } from '@Redux/search/interfaces';
import { setFilterItems, setTempFilterItems } from '@Redux/search/actions';
import { RootState } from '@Redux/search/store';
import SlidePage from '@components/Common/SlidePage';
import SlideFooter from '@components/Common/SlideFooter';
import FilterBlock from './subComponent/FilterBlock';

interface I_TitleKey {
    key: string;
    title: string;
    cols: number; // 幾個item一排
}
interface I_FiltersProps {
    isSlideOpen: boolean; // 是否啟動M版側開
    setSlideOpen: (isOpen: boolean) => void; // 讓M版篩選按紐啟動側開用callback
    onConfirm: (finalItems: I_FilterItem[]) => void; // M版按確認後
}

const travelTypeOptions = {
    [filterTypesKey.TravelType]: [
        {
            Text: '團體',
            Value: 1,
            note: '',
        },
        {
            Text: '團體自由行',
            Value: 2,
            note: '團體湊票享優惠',
        },
    ],
};

let filterTypesKeyArr: I_TitleKey[] = [
    // bbc 旅遊型態 在searchPanel上
    {
        key: filterTypesKey.TravelType,
        title: '旅遊型態',
        cols: 1, // checkbox 看要一欄還是兩欄
    },
    {
        key: filterTypesKey.DepartureList,
        title: '出發地',
        cols: 2,
    },
    {
        key: filterTypesKey.PriceRange,
        title: '預算',
        cols: 1,
    },
    {
        key: filterTypesKey.DayList,
        title: '旅遊天數',
        cols: 2,
    },
    {
        key: filterTypesKey.WeekDays,
        title: '出發星期',
        cols: 2,
    },
    {
        key: filterTypesKey.AirlineList,
        title: '航空公司',
        cols: 1,
    },
];

// 網址列有出發地，則篩選不顯示出發地選項
const urlParams = new URLSearchParams(window.location.search);
const DepartureID = urlParams.get('DepartureID');

if (DepartureID) {
    filterTypesKeyArr = filterTypesKeyArr.filter(
        (type) => type.key !== filterTypesKey.DepartureList
    );
}

const Filters: React.FC<I_FiltersProps> = ({ isSlideOpen, setSlideOpen, onConfirm }) => {
    const moduleName = 'filters';
    const dispatch = useDispatch();
    const { DepthInfo, FilterItems, TempFilterItems, SortListBy, TagFiltersShow } = useSelector(
        (state: RootState) => state.search
    );
    const isMobile = useMediaQuery({ query: '(max-width: 980px)' });
    // 當網址列有 TravelType(旅遊型態)時，則初始為網址列值
    useEffect(() => {
        dispatch(setFilterItems(TravelTypeItems));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const TravelTypeItems = (() => {
        const TravelType = new URLSearchParams(window.location.search).get('TravelType');
        const filterItems = [];

        const filterItem = (TravelType: string) => ({
            filterBy: 'TravelType',
            text: TravelType === '1' ? '團體' : '團體自由行',
            value: TravelType,
            subValue: '',
        });

        switch (TravelType) {
            case '1':
                filterItems.push(filterItem('1'));
                break;
            case '2':
                filterItems.push(filterItem('2'));
                break;
            // TravelType 為 0 或 null 時，團體 & 團體自由行都包含
            default:
                filterItems.push(filterItem('1'));
                filterItems.push(filterItem('2'));
                break;
        }
        return filterItems;
    })();

    const renderFilterBlocks = () => {
        return (
            <ul className={styles.filterBlocks}>
                {filterTypesKeyArr.map((item) => {
                    const commonProps = {
                        cols: item.cols,
                        title: item.title,
                        filterType: item.key,
                        options: {
                            [item.key]: DepthInfo[item.key],
                        },
                    };

                    switch (item.key) {
                        case filterTypesKey.AirlineList:
                        case filterTypesKey.DepartureList:
                        case filterTypesKey.DayList:
                        case filterTypesKey.WeekDays:
                            if (!DepthInfo[item.key]) return; // 如果資料沒有此key不渲染區塊
                            if (DepthInfo[item.key].length === 0) return; // 篩選類別無子項則不渲染區塊
                            break;
                        case filterTypesKey.PriceRange:
                            if (!DepthInfo[item.key]) return;
                            commonProps.options = {
                                [filterTypesKey.PriceRange]: [
                                    {
                                        PriceStart: DepthInfo[item.key].PriceStart,
                                        PriceEnd: DepthInfo[item.key].PriceEnd,
                                    },
                                ],
                            };
                            break;
                        case filterTypesKey.TravelType:
                            commonProps.options = travelTypeOptions;
                            break;
                        default:
                            return;
                    }
                    return <FilterBlock key={item.key} data_cy={item.key} {...commonProps} />;
                })}
            </ul>
        );
    };
    return DepthInfo ? (
        <div className={cN(moduleName, styles[`${moduleName}`])}>
            {DepthInfo &&
                (isMobile ? (
                    <div className={styles[`${moduleName}_M`]}>
                        <SlidePage
                            title="篩選"
                            visible={isSlideOpen}
                            setIsVisible={() => {
                                setSlideOpen(false);
                                dispatch(setFilterItems(TempFilterItems));
                            }}
                        >
                            <>
                                <div className={styles.SlideContent}>{renderFilterBlocks()}</div>
                                <SlideFooter
                                    onClickLeftBtn={() => {
                                        const notTagFiltersItems = FilterItems.filter(
                                            (item: I_FilterItem) =>
                                                item.filterBy === filterTypesKey.CategoryList
                                        );
                                        dispatch(setFilterItems(notTagFiltersItems));
                                    }}
                                    onClickRightBtn={() => {
                                        setSlideOpen(false);
                                        dispatch(setTempFilterItems(FilterItems));
                                        onConfirm(FilterItems);

                                        dispatch(setTempFilterItems(FilterItems));
                                    }}
                                    leftBtnText="重設條件"
                                    rightBtnText="確定"
                                />
                            </>
                        </SlidePage>
                    </div>
                ) : (
                    renderFilterBlocks()
                ))}
        </div>
    ) : null;
};

export default Filters;
